import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { toast } from 'react-hot-toast';

const collaboratorGeniusSBIData = {
  __metadata: {
    immutableFields: [
      'examples',
      'examples.*.title',
      'examples.*.situation',
      'examples.*.behavior',
      'examples.*.impact',
      'takeaways',
      'takeaways.strengths',
      'takeaways.areasForGrowth',
      'takeaways.actionItems'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  examples: [
    {
      title: "Team Meeting Feedback",
      situation: "During the Monday team meeting last week, when discussing upcoming deadlines.",
      behavior: "You spoke assertively about reallocating resources to meet the tight timeline.",
      impact: "Your clarity and decisiveness helped the team feel confident about the plan, but some members felt their concerns weren't fully addressed."
    },
    {
      title: "Brainstorming Session",
      situation: "In the brainstorming session for the product launch on January 15th.",
      behavior: "You actively engaged by suggesting three innovative ideas and asking others for input.",
      impact: "Your involvement boosted team morale and encouraged quieter members to participate."
    },
    {
      title: "Client Presentation",
      situation: "During the client presentation on December 20th.",
      behavior: "You presented the data effectively but interrupted a colleague mid-explanation.",
      impact: "While your expertise was evident, the interruption made the presentation feel less collaborative to the client."
    }
  ],
  takeaways: {
    strengths: [
      "Strong ability to drive decisions and provide clear direction",
      "Active participation and encouragement of team involvement"
    ],
    areasForGrowth: [
      "Balance assertiveness with inclusive decision-making",
      "Practice active listening and avoid interrupting others"
    ],
    actionItems: [
      "Implement a \"pause and reflect\" practice before responding in meetings",
      "Schedule regular check-ins to gather team feedback on communication style"
    ]
  }
};

const collaboratorGeniusConflictResData = {
  __metadata: {
    immutableFields: [
      'skills',
      'skills.*.title',
      'skills.*.selfScore',
      'skills.*.evaluatorScore',
      'skills.*.description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  skills: [
    {
      title: "Compromise",
      selfScore: 80,
      evaluatorScore: 70,
      description: "Michael sees himself as highly adaptable, but evaluators suggest there's slight room for improving mutual agreements in conflict situations."
    },
    {
      title: "Assertiveness",
      selfScore: 75,
      evaluatorScore: 65,
      description: "Michael feels confident expressing his ideas, but evaluators suggest ensuring assertiveness doesn't overpower collaboration."
    },
    {
      title: "Active Listening",
      selfScore: 65,
      evaluatorScore: 50,
      description: "Michael believes he listens attentively, but evaluators perceive gaps in validating and responding to others' input effectively."
    },
    {
      title: "Problem-Solving",
      selfScore: 85,
      evaluatorScore: 80,
      description: "Michael's strong problem-solving skills are closely aligned with evaluators' feedback, highlighting this as a key strength."
    }
  ]
};

const collaboratorGeniusLeadFollowData = {
  __metadata: {
    immutableFields: [
      'leadership',
      'leadership.effectiveness',
      'leadership.description',
      'followership',
      'followership.effectiveness',
      'followership.description',
      'recommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  leadership: {
    effectiveness: 60,
    description: "Michael takes initiative and demonstrates strong decision-making skills when leading projects, inspiring team alignment."
  },
  followership: {
    effectiveness: 40,
    description: "While Michael supports team efforts effectively, there is an opportunity to adapt and empower others more consistently when not in a leadership role."
  },
  recommendations: [
    "Practice active listening and empowerment when in supporting roles",
    "Develop situational awareness to better gauge when to lead vs. support",
    "Focus on building others' capabilities when in leadership positions"
  ]
};

const collaboratorGeniusCollabCapData = {
  __metadata: {
    immutableFields: [
      'capabilities',
      'capabilities.*.title',
      'capabilities.*.score',
      'capabilities.*.description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  capabilities: [
    {
      title: "Task Coordination",
      score: 80,
      description: "Michael is organized and ensures tasks are efficiently managed. Some feedback suggests enhancing communication to ensure all team members are aligned on priorities."
    },
    {
      title: "Participation",
      score: 70,
      description: "Michael actively engages in discussions and decisions, but occasional reluctance to take on certain roles or responsibilities is noted."
    },
    {
      title: "Shared Goal Alignment",
      score: 85,
      description: "Michael consistently aligns his work with the team's goals, ensuring his contributions drive collective success."
    },
    {
      title: "Supportiveness",
      score: 68,
      description: "While Michael supports his peers, evaluators noted opportunities to proactively offer assistance and encouragement in high-stakes scenarios."
    }
  ]
};

const collaboratorGeniusOverviewData = {
  __metadata: {
    immutableFields: [
      'metrics',
      'metrics.Trust',
      'metrics.Role Clarity',
      'metrics.Collaboration Frequency', 
      'metrics.Influence',
      'insights',
      'insights.Trust',
      'insights.Role Clarity',
      'insights.Collaboration Frequency',
      'insights.Influence'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  metrics: {
    Trust: 4.2,
    "Role Clarity": 3.8,
    "Collaboration Frequency": 4.5,
    Influence: 3.9
  },
  insights: {
    Trust: "Team members see Michael as dependable and consistent in his actions.",
    "Role Clarity": "While roles are generally well-defined, there's room for improvement in communicating individual responsibilities clearly.",
    "Collaboration Frequency": "Michael frequently engages with his team, ensuring consistent participation in collaborative tasks.",
    Influence: "Michael demonstrates effective influence in guiding decisions but could focus on aligning diverse perspectives more cohesively."
  }
};


const careerBrandingData = {
  __metadata: {
    immutableFields: [
      'cvKeywords',
      'linkedinRecommendations',
      'linkedinRecommendations.headline',
      'linkedinRecommendations.summary',
      'linkedinRecommendations.skills',
      'interviewKeywords'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  cvKeywords: [
    "Strategic Leadership",
    "Product Roadmap",
    "Cross-Functional Collaboration",
    "Revenue Growth",
    "Stakeholder Management"
  ],
  linkedinRecommendations: {
    headline: "Add 'Strategic Product Leader' or 'Driving Product Growth and Innovation' to your headline.",
    summary: "Incorporate keywords like 'scaling product portfolios,' 'delivering revenue growth,' and 'driving cross-functional collaboration.'",
    skills: ["Product Management", "Strategic Planning", "Data Analytics", "Team Leadership"]
  },
  interviewKeywords: [
    "Customer-Centric",
    "Data-Driven Decision Making",
    "Innovation",
    "Scalable Solutions",
    "Revenue Optimization"
  ]
};

const careerResilienceData = {
  __metadata: {
    immutableFields: [
      'resilienceTraits',
      'resilienceTraits.*.name',
      'resilienceTraits.*.score',
      'resilienceInsights',
      'resilienceInsights.strengths',
      'resilienceInsights.areasForImprovement',
      'recommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  resilienceTraits: {
    "Adaptability": 4.2,
    "Resourcefulness": 3.8,
    "Growth Mindset": 3.5
  },
  resilienceInsights: {
    strengths: [
      "You excel in adapting to new challenges and environments, demonstrating flexibility in uncertain situations."
    ],
    areasForImprovement: [
      "Your growth mindset could be strengthened by actively seeking feedback and embracing opportunities for learning."
    ]
  },
  recommendations: [
    "Practice mindfulness to improve stress management during career transitions.",
    "Engage in case-study exercises to enhance problem-solving under pressure.",
    "Take a course on industry trends to prepare for potential pivots."
  ]
};

const industryAlignmentData = {
  __metadata: {
    immutableFields: [
      'industryTrends',
      'industryTrends.*.name',
      'industryTrends.*.score',
      'industryTrends.*.status',
      'alignmentScore',
      'areasForDevelopment'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  industryTrends: [
    {
      name: "Data Analytics",
      score: 80,
      status: "aligned"
    },
    {
      name: "AI Knowledge",
      score: 33,
      status: "development_needed"
    },
    {
      name: "Stakeholder Management",
      score: 80,
      status: "aligned"
    }
  ],
  alignmentScore: 75,
  areasForDevelopment: [
    {
      skill: "AI Knowledge",
      description: "Consider upskilling in this area to stay competitive"
    }
  ]
};

const careerSkillGapData = {
  __metadata: {
    immutableFields: [
      'currentSkills',
      'requiredSkills',
      'keyInsights',
      'developmentActions'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  currentSkills: {
    Leadership: 3,
    "Strategic Planning": 2,
    "Data Analysis": 4,
    "Stakeholder Communication": 3
  },
  requiredSkills: {
    Leadership: 5,
    "Strategic Planning": 4,
    "Data Analysis": 4,
    "Stakeholder Communication": 5
  },
  keyInsights: [
    "Your data analysis skills are well-aligned with role requirements",
    "Focus on developing strategic planning capabilities for future roles",
    "Leadership skills need enhancement to meet Director-level expectations"
  ],
  developmentActions: [
    "Enroll in leadership development programs",
    "Seek strategic planning opportunities in current role",
    "Build mentorship relationships with senior leaders"
  ]
};

const careerChallengesData = {
  __metadata: {
    immutableFields: [
      'challenges'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  challenges: [
    "Enhance strategic planning by leading cross-functional projects.",
    "Commanding leadership amongst other senior members of the team.",
    "Develop leadership by mentoring junior team members."
  ]
};

const careerReadinessData = {
  __metadata: {
    immutableFields: [
      'readinessScore'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  readinessScore: 78
};

const careerAcceleratorRolesData = {
  __metadata: {
    immutableFields: [
      'currentRole',
      'currentRole.title',
      'currentRole.duration',
      'desiredRole',
      'desiredRole.title',
      'desiredRole.timeToTransition'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  currentRole: {
    title: "Senior Product Manager",
    duration: "1 year 3 months"
  },
  desiredRole: {
    title: "Director of Product",
    timeToTransition: "2 years"
  }
};

const developmentPlanData = {
  __metadata: {
    immutableFields: [
      'shortTerm',
      'shortTerm.*.goal',
      'shortTerm.*.action',
      'longTerm',
      'longTerm.*.goal',
      'longTerm.*.action'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  shortTerm: [
    { goal: "Enhance delegation skills", action: "Participate in a delegation workshop." },
    { goal: "Improve non-verbal communication", action: "Practice body language in presentation rehearsals." }
  ],
  longTerm: [
    { goal: "Develop a personal leadership philosophy", action: "Engage in executive coaching sessions." },
    { goal: "Foster collaborative decision-making", action: "Host regular team brainstorming sessions." }
  ]
};

const executivePresenceData = {
  __metadata: {
    immutableFields: [
      'components',
      'components.*.name',
      'components.*.score',
      'components.*.percentage',
      'observations',
      'observations.*.text',
      'strategies'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  components: [
    { name: "Confidence", score: 8.0, percentage: 40 },
    { name: "Communication Clarity", score: 7.0, percentage: 35 },
    { name: "Non-Verbal Communication", score: 5.0, percentage: 25 }
  ],
  observations: [
    { text: "Your confidence is a key strength, giving you a strong presence in meetings and presentations." },
    { text: "Non-verbal communication, such as body language, scored lower, indicating room for improvement." }
  ],
  strategies: [
    "Practice power poses before important presentations",
    "Record and analyze your presentation style for improvement"
  ]
};

const emotionalIntelligenceData = {
  __metadata: {
    immutableFields: [
      'categories',
      'scores',
      'analysis',
      'developmentAreas'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  categories: ["Self-Awareness", "Self-Regulation", "Empathy", "Motivation", "Social Skills"],
  scores: [3.8, 3.5, 4.6, 4.2, 4.0],
  analysis: [
    {
      category: "Self-Awareness",
      text: "Empathy is a significant strength, reflecting your ability to understand and connect with others effectively."
    },
    {
      category: "Self-Regulation",
      text: "Self-regulation scored lower, suggesting a need to manage emotions better during high-pressure situations."
    }
  ],
  developmentAreas: [
    "Practice mindfulness techniques for better emotional regulation",
    "Develop strategies for maintaining composure in challenging situations"
  ]
};

const leadershipProfileData = {
  __metadata: {
    immutableFields: [
      'leadershipProfile.traits',
      'leadershipProfile.traits.*.name',
      'leadershipProfile.traits.*.selfScore',
      'leadershipProfile.traits.*.evaluatorScore',
      'leadershipProfile.keyInsights',
      'leadershipProfile.recommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  leadershipProfile: {
    traits: [
      { name: "Vision", selfScore: 4.2, evaluatorScore: 3.9 },
      { name: "Adaptability", selfScore: 4.5, evaluatorScore: 4.8 },
      { name: "Decision-Making", selfScore: 3.8, evaluatorScore: 3.5 },
      { name: "Delegation", selfScore: 3.0, evaluatorScore: 2.7 },
      { name: "Influence", selfScore: 4.7, evaluatorScore: 4.6 }
    ],
    keyInsights: [
      "Your adaptability is a standout strength, highly rated by both yourself and evaluators.",
      "Delegation scored lower, indicating an opportunity to involve your team more effectively in task ownership."
    ],
    recommendations: [
      "Leverage your strong adaptability to lead change initiatives",
      "Focus on developing delegation skills through structured practice"
    ]
  },
  generatedAt: serverTimestamp()
};

export default function AdvancedPlansDataTester() {
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    setLoading(true);

    try {
      const reportRef = doc(db, collections.reports, 'example-data');
      
      // Add career resilience and industry alignment data
      await setDoc(reportRef, {
        collaborator_genius_collab_cap: JSON.stringify(collaboratorGeniusCollabCapData),
        collaborator_genius_lead_follow: JSON.stringify(collaboratorGeniusLeadFollowData),
        collaborator_genius_sbi: JSON.stringify(collaboratorGeniusSBIData),
        collaborator_genius_conflict_res: JSON.stringify(collaboratorGeniusConflictResData),
        career_accelerator_branding: JSON.stringify(careerBrandingData),
        career_accelerator_resilience: JSON.stringify(careerResilienceData),
        career_accelerator_alingment: JSON.stringify(industryAlignmentData),
        collaborator_genius_overview: JSON.stringify(collaboratorGeniusOverviewData)
      }, { merge: true });

      await setDoc(reportRef, {
        career_accelerator_skill_gap: JSON.stringify(careerSkillGapData),
        career_accelerator_readiness_challenges: JSON.stringify(careerChallengesData),
        career_accelerator_readiness_est: JSON.stringify(careerReadinessData),
        career_accelerator_roles: JSON.stringify(careerAcceleratorRolesData),
        leadership_edge_plan: JSON.stringify(developmentPlanData),
        leadership_edge_executive_presence: JSON.stringify(executivePresenceData),
        leadership_edge_eq: JSON.stringify(emotionalIntelligenceData),
        leadership_edge_leadership_profile: JSON.stringify(leadershipProfileData)
      }, { merge: true });
      toast.success('Leadership data generated successfully');
    } catch (error: any) {
      console.error('Error generating leadership profile data:', error);
      toast.error(error.message || 'Failed to generate leadership profile data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handleGenerateReport}
        disabled={loading}
        className="w-full bg-primary text-dark px-4 py-2 rounded hover:bg-primary-hover transition-colors disabled:opacity-50"
      >
        {loading ? 'Generating Leadership Data...' : 'Generate Leadership Data'}
      </button>
    </div>
  );
}